import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { SiteProvider } from "@/context/SiteContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { Spinner } from "@/components/common";
import Home from "@/pages/Home";
import Collection from "@/pages/Collection";
import Product from "@/pages/Product";
import RingBuilder from "@/pages/RingBuilder";
import Checkout from "@/pages/Checkout";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { InstalmentTest, InstalmentReturn } from "@/pages/InstalmentPages";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";
import Account from "@/pages/Account";
import Unsubscribe from "@/pages/Unsubscribe";
import ReviewQuote from "@/pages/ReviewQuote";
import Track from "@/pages/Track";
import Financing from "@/pages/Financing";
import Consultation from "@/pages/Consultation";
import MemorialEnquiry from "@/pages/MemorialEnquiry";
import HireEnquiry from "@/pages/HireEnquiry";
import { WhatsAppButton } from "@/components/WhatsApp";
import Stores from "@/pages/Stores";
import { CmsPage, EducationIndex, BlogPost, FaqPage, NotFound } from "@/pages/Content";
import Blog from "@/pages/Blog";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminOrders from "@/admin/AdminOrders";
import AdminProducts from "@/admin/AdminProducts";
import AdminSync from "@/admin/AdminSync";
import AdminSite from "@/admin/AdminSite";
import AdminReviews from "@/admin/AdminReviews";
import AdminRewards from "@/admin/AdminRewards";
import BankTransferPlaced from "@/pages/BankTransferPlaced";
import RingSizer from "@/pages/RingSizer";
import AdminInbox from "@/admin/AdminInbox";
import AdminUsers from "@/admin/AdminUsers";
import ResourceAdmin from "@/admin/ResourceAdmin";
import AdminSeo from "@/admin/AdminSeo";
import ReplacedPhotos from "@/admin/ReplacedPhotos";
import AdminProvenance from "@/admin/AdminProvenance";
import LoanRings from "@/admin/LoanRings";
import StoriesAdmin from "@/admin/StoriesAdmin";
import CertificatePrint from "@/pages/CertificatePrint";
import ProposalPlanner from "@/pages/ProposalPlanner";
import StoryPage from "@/pages/StoryPage";
import DesignReview from "@/pages/DesignReview";
import MakingOf from "@/pages/MakingOf";
import ReelPage from "@/pages/ReelPage";
import AdminStyleTiles from "@/admin/AdminStyleTiles";
import AdminInstalments from "@/admin/AdminInstalments";
import AdminGiftGuide from "@/admin/AdminGiftGuide";
import AdminGiveaway from "@/admin/AdminGiveaway";
import AdminPopup from "@/admin/AdminPopup";
import { NewsletterPopup } from "@/components/NewsletterPopup";
import GiftGuide from "@/pages/GiftGuide";
import AdminPageNav from "@/admin/AdminPageNav";
import StylePage from "@/pages/StylePage";
import Provenance, { ProvenanceConfirm } from "@/pages/Provenance";

function ScrollTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    let n = 0;
    const t = setInterval(() => {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el || ++n > 25) { clearInterval(t); if (el) el.scrollIntoView(); else window.scrollTo(0, 0); }
    }, 100);
    return () => clearInterval(t);
  }, [pathname, hash]);
  return null;
}

function Shop() {
  return (
    <div className="storefront grain min-h-screen flex flex-col">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <WhatsAppButton />
      <CartDrawer />
      <NewsletterPopup />
    </div>
  );
}

export function RequireAuth({ admin = false, children }: { admin?: boolean; children: React.ReactNode }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/account" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <SiteProvider>
        <AuthProvider>
          <CartProvider>
            <ScrollTop />
            <Routes>
              <Route element={<Shop />}>
                <Route path="/" element={<Home />} />
                <Route path="/collections" element={<CmsPage slug="collections" />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/review/:id" element={<ReviewQuote />} />
                <Route path="/collections/:slug" element={<Collection />} />
                <Route path="/search" element={<Collection />} />
                <Route path="/products/:slug" element={<Product />} />
                <Route path="/ring-builder" element={<RingBuilder />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/payment/instalments/test" element={<InstalmentTest />} />
                <Route path="/payment/instalments" element={<InstalmentReturn />} />
                <Route path="/payment/bank-transfer" element={<BankTransferPlaced />} />
                <Route path="/ring-sizer" element={<RingSizer />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
                <Route path="/track" element={<Track />} />
                <Route path="/financing" element={<Financing />} />
                <Route path="/gift-guide" element={<GiftGuide />} />
                <Route path="/consultation" element={<Consultation />} />
                <Route path="/memorial-enquiry" element={<MemorialEnquiry />} />
                <Route path="/hire-enquiry" element={<HireEnquiry />} />
                <Route path="/stores" element={<Stores />} />
                <Route path="/education" element={<EducationIndex />} />
                <Route path="/education/:slug" element={<CmsPage />} />
                <Route path="/pages/:slug" element={<CmsPage />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/provenance/confirm/:ctoken" element={<ProvenanceConfirm />} />
                <Route path="/provenance/:token" element={<Provenance />} />
                <Route path="/proposal-planner" element={<ProposalPlanner />} />
                <Route path="/stories/:id" element={<StoryPage />} />
                <Route path="/design/:token" element={<DesignReview />} />
                <Route path="/provenance/:token/making-of" element={<MakingOf />} />
                <Route path="/reel/:token" element={<ReelPage />} />
                <Route path="/collections/:slug/style/:style" element={<StylePage />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/certificate/:id" element={<RequireAuth admin><CertificatePrint /></RequireAuth>} />
              <Route path="/admin" element={<RequireAuth admin><AdminLayout /></RequireAuth>}>
                <Route index element={<AdminDashboard />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="sync" element={<AdminSync />} />
                <Route path="site" element={<AdminSite />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="rewards" element={<AdminRewards />} />
                <Route path="inbox" element={<AdminInbox />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="seo" element={<AdminSeo />} />
                <Route path="replaced-photos" element={<ReplacedPhotos />} />
                <Route path="provenance" element={<AdminProvenance />} />
                <Route path="loan-rings" element={<LoanRings />} />
                <Route path="stories" element={<StoriesAdmin />} />
                <Route path="style-tiles" element={<AdminStyleTiles />} />
                <Route path="instalments" element={<AdminInstalments />} />
                <Route path="gift-guide" element={<AdminGiftGuide />} />
                <Route path="giveaway" element={<AdminGiveaway />} />
                <Route path="signup-popup" element={<AdminPopup />} />
                <Route path="page-nav" element={<AdminPageNav />} />
                <Route path="content/redirects" element={<Navigate to="/admin/seo?tab=redirects" replace />} />
                <Route path="content/:res" element={<ResourceAdmin />} />
              </Route>
            </Routes>
            <Toaster position="bottom-center" />
          </CartProvider>
        </AuthProvider>
      </SiteProvider>
    </BrowserRouter>
  );
}
